// Paddle.js v2 integration. Paddle.js itself is loaded via a <script> tag in
// index.html and exposed on window.Paddle.
// Docs: https://developer.paddle.com/paddlejs/overview

export interface PaddleInitConfig {
    clientToken: string;
    environment: 'sandbox' | 'production';
}

export interface PaddleCheckoutConfig {
    priceId: string;
    customerEmail: string;
    customData: {
        organizationId: string;
        userId: string;
        // Server-signed token (from the checkout session) proving this checkout
        // was authorized for organizationId. Verified by the webhook.
        orgToken: string;
    };
    onComplete: () => void;
    onClose: () => void;
}

// Minimal typing for the parts of Paddle.js v2 we use.
interface PaddleGlobal {
    Environment: {set: (env: 'sandbox' | 'production') => void};
    Initialize: (config: {token: string; eventCallback?: (event: {name: string}) => void}) => void;
    Checkout: {
        open: (config: Record<string, unknown>) => void;
    };
}

declare global {
    interface Window {
        Paddle?: PaddleGlobal;
    }
}

// Active checkout callbacks, dispatched from the global Paddle eventCallback.
let activeCheckout: {onComplete: () => void; onClose: () => void} | null = null;

// Remember the token we initialised with so re-initialising for the same org is
// a no-op, but a different token (org) re-initialises correctly.
let initializedToken: string | null = null;

export const PaddleService = {
    isAvailable(): boolean {
        return typeof window !== 'undefined' && !!window.Paddle;
    },

    initialize(config: PaddleInitConfig): void {
        if (!window.Paddle) {
            console.warn('[Paddle] Paddle.js not loaded; checkout is unavailable');
            return;
        }
        if (initializedToken === config.clientToken) return;

        window.Paddle.Environment.set(config.environment);
        window.Paddle.Initialize({
            token: config.clientToken,
            eventCallback: (event) => {
                if (event.name === 'checkout.completed') {
                    activeCheckout?.onComplete();
                } else if (event.name === 'checkout.closed') {
                    activeCheckout?.onClose();
                    activeCheckout = null;
                }
            },
        });
        initializedToken = config.clientToken;
    },

    openCheckout(config: PaddleCheckoutConfig): void {
        if (!window.Paddle) {
            throw new Error('Paddle.js is not loaded');
        }

        activeCheckout = {onComplete: config.onComplete, onClose: config.onClose};

        window.Paddle.Checkout.open({
            items: [{priceId: config.priceId, quantity: 1}],
            customer: {email: config.customerEmail},
            customData: config.customData,
            settings: {
                displayMode: 'overlay',
                theme: 'light',
                // No successUrl: a successUrl makes Paddle.js redirect the tab on
                // completion, tearing down the SPA before the checkout.completed
                // handler + post-checkout polling can run. We let the overlay show
                // its success screen and drive state from the event instead.
            },
        });
    },
};

export default PaddleService;
